import { useQuery } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

export type FinanceTaxStatement = {
  id: string;
  tax_year: number;
  user_id: string | null;
  file_name: string;
  display_name: string | null;
  donor_name: string | null;
  donor_email: string | null;
  file_size_bytes: number | null;
  generated_at: string | null;
  created_at: string;
};

type ListResponse = {
  statements?: FinanceTaxStatement[];
  error?: string;
};

type DownloadResponse = {
  signedUrl?: string;
  fileName?: string;
  error?: string;
};

export type GenerateFinanceTaxStatementsMode = 'skip_existing' | 'replace_existing';
export type GenerateFinanceTaxStatementsDonorType = 'user' | 'email' | 'name';

export type GenerateFinanceTaxStatementsDonorScope = {
  donorType: GenerateFinanceTaxStatementsDonorType;
  donorValue: string;
};

export type GenerateFinanceTaxStatementsResult = {
  taxYear: number;
  mode: GenerateFinanceTaxStatementsMode;
  created: number;
  replaced: number;
  skipped: number;
  failed: number;
  totalDonors: number;
  errors: string[];
};

type GenerateResponse = Partial<GenerateFinanceTaxStatementsResult> & {
  error?: string;
};

export function useFinanceTaxStatements(enabled: boolean) {
  return useQuery({
    queryKey: ['finance', 'tax-statements'],
    queryFn: async (): Promise<FinanceTaxStatement[]> => {
      const { data, error } = await supabase.functions.invoke('finance-tax-statements', {
        body: { action: 'list' },
      });
      if (error) throw error;
      const payload = (data ?? {}) as ListResponse;
      if (payload.error) {
        throw new Error(payload.error);
      }
      return payload.statements ?? [];
    },
    enabled,
  });
}

export async function getTaxStatementDownloadUrl(statementId: string): Promise<{ url: string; fileName: string }> {
  const { data, error } = await supabase.functions.invoke('finance-tax-statements', {
    body: { action: 'download', statementId },
  });
  if (error) throw error;
  const payload = (data ?? {}) as DownloadResponse;
  if (payload.error || !payload.signedUrl || !payload.fileName) {
    throw new Error(payload.error ?? 'Failed to create statement download URL.');
  }
  return { url: payload.signedUrl, fileName: payload.fileName };
}

export async function generateFinanceTaxStatements(
  taxYear: number,
  mode: GenerateFinanceTaxStatementsMode,
  donorScope?: GenerateFinanceTaxStatementsDonorScope
): Promise<GenerateFinanceTaxStatementsResult> {
  const { data, error } = await supabase.functions.invoke('finance-tax-statements', {
    body: {
      action: 'generate',
      taxYear,
      mode,
      ...(donorScope
        ? {
            donorType: donorScope.donorType,
            donorValue: donorScope.donorValue,
          }
        : {}),
    },
  });
  if (error) throw error;
  const payload = (data ?? {}) as GenerateResponse;
  if (payload.error) {
    throw new Error(payload.error);
  }

  return {
    taxYear: Number(payload.taxYear ?? taxYear),
    mode: (payload.mode as GenerateFinanceTaxStatementsMode) || mode,
    created: Number(payload.created ?? 0),
    replaced: Number(payload.replaced ?? 0),
    skipped: Number(payload.skipped ?? 0),
    failed: Number(payload.failed ?? 0),
    totalDonors: Number(payload.totalDonors ?? 0),
    errors: Array.isArray(payload.errors) ? payload.errors.map((entry) => String(entry)) : [],
  };
}
